import { useCallback, useEffect, useRef } from "react";

// Shared between the connect dialogs and the callback page.
export const GOOGLE_OAUTH_CHANNEL = "markezy-google-oauth";
const STORAGE_KEY = "markezy-google-oauth-result";

/**
 * Called from the callback page (inside the popup) to report the outcome.
 *
 * Google's sign-in pages send a Cross-Origin-Opener-Policy header, so once the
 * popup has visited accounts.google.com the browser severs it from its opener:
 * `window.opener` is null by the time we land back here. BroadcastChannel (and
 * the storage event as a fallback) only need the same origin, so they still
 * reach the dialog.
 */
export function reportGoogleOAuthResult({ connected, error }) {
  const payload = { source: GOOGLE_OAUTH_CHANNEL, connected, error, at: Date.now() };

  try {
    const channel = new BroadcastChannel(GOOGLE_OAUTH_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  } catch {
    // BroadcastChannel unsupported; the storage event below covers it.
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage blocked; nothing else to try.
  }

  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(payload, window.location.origin);
    }
  } catch {
    // Opener gone or cross-origin.
  }
}

/**
 * Opens the Google OAuth popup and listens for the result while `active`.
 *
 * `popup.closed` is not used to detect an abandoned flow: after the COOP swap
 * described above the opener sees the popup as closed while it is still on
 * Google's consent screen. Instead, when this tab regains focus without a
 * result, `onAbandoned` runs so the dialog can stop waiting and refresh.
 */
export function useGoogleOAuthPopup({ active, onResult, onAbandoned }) {
  const waitingRef = useRef(false);
  const lastResultAtRef = useRef(0);
  const onResultRef = useRef(onResult);
  const onAbandonedRef = useRef(onAbandoned);
  onResultRef.current = onResult;
  onAbandonedRef.current = onAbandoned;

  useEffect(() => {
    if (!active) return undefined;

    const handle = (data) => {
      if (data?.source !== GOOGLE_OAUTH_CHANNEL) return;
      // The same result can arrive through more than one transport.
      if (data.at && data.at === lastResultAtRef.current) return;
      lastResultAtRef.current = data.at;
      waitingRef.current = false;
      onResultRef.current?.(data);
    };

    let channel = null;
    try {
      channel = new BroadcastChannel(GOOGLE_OAUTH_CHANNEL);
      channel.onmessage = (event) => handle(event.data);
    } catch {
      channel = null;
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      handle(event.data);
    };

    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY || !event.newValue) return;
      try {
        handle(JSON.parse(event.newValue));
      } catch {
        // Ignore malformed values.
      }
    };

    let focusTimeout = null;
    const handleFocus = () => {
      if (!waitingRef.current) return;
      clearTimeout(focusTimeout);
      // Give a result that is already on its way a moment to land.
      focusTimeout = setTimeout(() => {
        if (!waitingRef.current) return;
        waitingRef.current = false;
        onAbandonedRef.current?.();
      }, 1500);
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", handleFocus);
    return () => {
      channel?.close();
      window.removeEventListener("message", handleMessage);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", handleFocus);
      clearTimeout(focusTimeout);
    };
  }, [active]);

  /** Returns false when the browser blocked the popup. */
  const openPopup = useCallback((url) => {
    const popup = window.open(
      url,
      "google-oauth",
      "width=520,height=680,menubar=no,toolbar=no"
    );
    if (!popup) return false;
    waitingRef.current = true;
    return true;
  }, []);

  return { openPopup };
}
